import { wencesApi } from "@/api/wencesApi";

// Cache para el formato de números
let numberFormatCache = null;
let formatLoadPromise = null;

/**
 * Obtiene el formato de números desde settings
 * @returns {Promise<string>} Formato en formato "thousandsSeparator.decimalSeparator.decimals"
 */
export async function getNumberFormat() {
  // Si ya tenemos el formato en cache, retornarlo
  if (numberFormatCache) {
    return numberFormatCache;
  }

  // Si ya hay una petición en curso, esperar a que termine
  if (formatLoadPromise) {
    return formatLoadPromise;
  }

  // Intentar obtener desde localStorage primero
  const cachedFormat = localStorage.getItem("numberFormat");
  if (cachedFormat) {
    numberFormatCache = cachedFormat;
    return cachedFormat;
  }

  // Si no está en localStorage, cargar desde la API
  formatLoadPromise = (async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await wencesApi.get("/api/settings", {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (response.data.success && response.data.data) {
        const format = response.data.data.numberFormat || "dot.comma.2";
        numberFormatCache = format;
        localStorage.setItem("numberFormat", format);
        return format;
      }
    } catch (error) {
      console.error("Error loading number format:", error);
    }
    // Fallback al formato por defecto
    const defaultFormat = "dot.comma.2";
    numberFormatCache = defaultFormat;
    localStorage.setItem("numberFormat", defaultFormat);
    return defaultFormat;
  })();

  return formatLoadPromise;
}

/**
 * Formatea un número según el formato configurado en settings
 * @param {number|string} value - El número a formatear
 * @param {string} [customFormat] - Formato personalizado opcional (override del formato de settings)
 * @param {boolean} [isCurrency] - Si es true, siempre muestra decimales (para valores monetarios)
 * @returns {Promise<string>} El número formateado
 */
export async function formatNumber(value, customFormat = null, isCurrency = false) {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  // Convertir a número si es string
  const numValue = typeof value === "string" ? parseFloat(value) : value;

  if (isNaN(numValue)) {
    return "";
  }

  // Verificar si el número es entero (sin decimales)
  const isInteger = Number.isInteger(numValue) || (numValue % 1 === 0);

  // Obtener el formato (usar customFormat si se proporciona, sino obtener de settings)
  const formatString = customFormat || (await getNumberFormat());
  const [thousandsSep, decimalSep, decimals] = formatString.split(".");

  const decimalsCount = parseInt(decimals, 10);

  // Si es currency, siempre mostrar decimales. Si es entero y no es currency, no mostrar decimales
  const actualDecimalsCount = isCurrency ? decimalsCount : (isInteger ? 0 : decimalsCount);

  // Formatear con decimales
  const fixed = numValue.toFixed(actualDecimalsCount);
  const parts = fixed.split(".");
  let integerPart = parts[0];
  let decimalPart = parts[1] || "";

  // Pad decimal part if needed (solo si no es entero)
  if (actualDecimalsCount > 0 && decimalPart.length < actualDecimalsCount) {
    decimalPart = decimalPart.padEnd(actualDecimalsCount, "0");
  }

  // Add thousands separator
  if (thousandsSep === "dot") {
    integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  } else if (thousandsSep === "comma") {
    integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  }
  // Si es "none", no agregar separador

  // Combine with decimal separator
  if (actualDecimalsCount > 0) {
    const decimalSymbol = decimalSep === "dot" ? "." : ",";
    return `${integerPart}${decimalSymbol}${decimalPart}`;
  } else {
    return integerPart;
  }
}

/** Clave del campo margen operación % (Duntower). */
export const OPERATION_MARGIN_PCT_FIELD_KEY = "operation_margin_pct";

/** Decimales fijos al mostrar/exportar operation_margin_pct (no usa field.decimals genérico). */
export const OPERATION_MARGIN_PCT_DISPLAY_DECIMALS = 2;

/**
 * Override de decimales solo para operation_margin_pct; null para cualquier otro campo.
 * @param {string|undefined|null} fieldKey
 * @returns {number|null}
 */
export function getDisplayDecimalsOverrideForFieldKey(fieldKey) {
  if (String(fieldKey || "") === OPERATION_MARGIN_PCT_FIELD_KEY) {
    return OPERATION_MARGIN_PCT_DISPLAY_DECIMALS;
  }
  return null;
}

/**
 * @param {string} formatString ej. dot.comma.4
 * @param {number|null|undefined} decimalsOverride
 * @returns {string}
 */
export function applyDecimalsToFormatString(formatString, decimalsOverride) {
  if (decimalsOverride == null || !Number.isFinite(Number(decimalsOverride))) {
    return formatString;
  }
  const parts = String(formatString || "dot.comma.2").split(".");
  if (parts.length < 3) {
    return formatString;
  }
  parts[2] = String(Math.max(0, parseInt(decimalsOverride, 10)));
  return parts.join(".");
}

/**
 * Versión síncrona que usa el formato en cache
 * Si el formato no está en cache, usa el formato por defecto
 * @param {number|string} value - El número a formatear
 * @param {string} [customFormat] - Formato personalizado opcional
 * @param {boolean} [isCurrency] - Si es true, siempre muestra decimales (para valores monetarios)
 * @param {number|null|undefined} [decimalsOverride] - Solo pasar vía getDisplayDecimalsOverrideForFieldKey (operation_margin_pct)
 * @returns {string} El número formateado
 */
export function formatNumberSync(value, customFormat = null, isCurrency = false, decimalsOverride = null) {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  // Convertir a número si es string
  const numValue = typeof value === "string" ? parseFloat(value) : value;

  if (isNaN(numValue)) {
    return "";
  }

  // Verificar si el número es entero (sin decimales)
  const isInteger = Number.isInteger(numValue) || (numValue % 1 === 0);

  // Obtener el formato desde cache o usar el proporcionado
  // Intentar cargar desde localStorage si no está en cache
  if (!numberFormatCache && !customFormat) {
    const cachedFormat = localStorage.getItem("numberFormat");
    if (cachedFormat) {
      numberFormatCache = cachedFormat;
    }
  }

  let formatString =
    customFormat ||
    numberFormatCache ||
    localStorage.getItem("numberFormat") ||
    "dot.comma.2";
  formatString = applyDecimalsToFormatString(formatString, decimalsOverride);

  // Debug: log para verificar el formato usado (siempre visible)
  if (!customFormat && (numValue === 177289 || numValue === 9.33 || numValue === 124102.30)) {
    console.log('🔢 FormatNumberSync DEBUG:', {
      value: numValue,
      format: formatString,
      cache: numberFormatCache,
      localStorage: localStorage.getItem("numberFormat"),
      finalFormat: formatString,
      isCurrency: isCurrency
    });
  }
  
  const [thousandsSep, decimalSep, decimals] = formatString.split(".");

  const decimalsCount = parseInt(decimals, 10);

  const hasDecimalsOverride =
    decimalsOverride != null && Number.isFinite(Number(decimalsOverride));

  // Override explícito (p. ej. operation_margin_pct → 2 decimales fijos).
  const actualDecimalsCount = hasDecimalsOverride
    ? Math.max(0, parseInt(decimalsOverride, 10))
    : isCurrency
      ? decimalsCount
      : isInteger
        ? 0
        : decimalsCount;

  // Formatear con decimales
  const fixed = numValue.toFixed(actualDecimalsCount);
  const parts = fixed.split(".");
  let integerPart = parts[0];
  let decimalPart = parts[1] || "";

  // Pad decimal part if needed (solo si no es entero)
  if (actualDecimalsCount > 0 && decimalPart.length < actualDecimalsCount) {
    decimalPart = decimalPart.padEnd(actualDecimalsCount, "0");
  }

  // Add thousands separator
  if (thousandsSep === "dot") {
    integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  } else if (thousandsSep === "comma") {
    integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  }
  // Si es "none", no agregar separador

  // Combine with decimal separator
  if (actualDecimalsCount > 0) {
    const decimalSymbol = decimalSep === "dot" ? "." : ",";
    return `${integerPart}${decimalSymbol}${decimalPart}`;
  } else {
    return integerPart;
  }
}

/**
 * Limpia el cache del formato de números
 * Útil cuando se actualiza el formato en settings
 */
export function clearNumberFormatCache() {
  numberFormatCache = null;
  formatLoadPromise = null;
  localStorage.removeItem("numberFormat");
}

/**
 * Establece el formato de números en cache
 * Útil para actualizar el cache cuando se guarda el formato en settings
 * @param {string} format - El formato a guardar
 */
export function setNumberFormatCache(format) {
  numberFormatCache = format;
  localStorage.setItem("numberFormat", format);
}

/**
 * Lee un número como se muestra en pantalla (p. ej. «1,338.0885 USD» copiado de un campo):
 * saca la moneda y el separador de miles, y deja el decimal como punto.
 * - Con coma y punto, el último que aparece es el decimal.
 * - Un separador repetido («1,338,000») es de miles.
 * - Uno solo es de miles si así lo dice Settings y le siguen justo 3 dígitos («1,338» en
 *   comma.dot); si no, es el decimal («5,5»).
 * @param {string} text
 * @param {string|null} [customFormat] - Override opcional "thousands.decimal.N"
 * @returns {string|null} número con punto decimal, o null si el texto no tiene dígitos
 */
export function parseDisplayedNumberSync(text, customFormat = null) {
  let s = String(text == null ? "" : text).replace(/[^0-9.,-]/g, "");
  if (!/[0-9]/.test(s)) return null;
  const negative = s.startsWith("-");
  s = s.replace(/-/g, "");

  const lastComma = s.lastIndexOf(",");
  const lastDot = s.lastIndexOf(".");
  let decimalSep = null;
  if (lastComma !== -1 && lastDot !== -1) {
    decimalSep = lastComma > lastDot ? "," : ".";
  } else if (lastComma !== -1 || lastDot !== -1) {
    const sep = lastComma !== -1 ? "," : ".";
    const other = sep === "," ? "." : ",";
    if (s.split(sep).length > 2) {
      decimalSep = other;
    } else {
      const format =
        customFormat || numberFormatCache || localStorage.getItem("numberFormat") || "dot.comma.2";
      const thousands = format.split(".")[0];
      const thousandsChar = thousands === "comma" ? "," : thousands === "dot" ? "." : null;
      const digitsAfter = s.length - s.indexOf(sep) - 1;
      decimalSep = sep === thousandsChar && digitsAfter === 3 ? other : sep;
    }
  }

  if (decimalSep) {
    const thousandsSep = decimalSep === "," ? "." : ",";
    s = s.split(thousandsSep).join("").replace(decimalSep, ".");
  }
  return (negative ? "-" : "") + s;
}

/**
 * Decimales para cálculos (mismo criterio que el formato en Settings: tercer segmento de numberFormat).
 * Síncrono: usa cache, localStorage o default — coherente con formatNumberSync.
 * @param {string|null} [customFormat] - Override opcional "thousands.decimal.N"
 * @returns {number} 0–20 (Settings UI usa 0,2,3,4)
 */
export function getCalculationDecimalPlaces(customFormat = null) {
  if (!numberFormatCache && !customFormat) {
    const cachedFormat = localStorage.getItem("numberFormat");
    if (cachedFormat) {
      numberFormatCache = cachedFormat;
    }
  }
  const formatString =
    customFormat ||
    numberFormatCache ||
    localStorage.getItem("numberFormat") ||
    "dot.comma.2";
  const parts = formatString.split(".");
  const n = parseInt(parts[2], 10);
  if (Number.isNaN(n) || n < 0) {
    return 2;
  }
  return Math.min(20, n);
}

/**
 * Redondea un número según decimales del formato de sistema (Settings).
 * @param {number} value
 * @param {number|null} [decimalPlaces] - Si es null/undefined, usa getCalculationDecimalPlaces()
 */
export function roundNumberBySettings(value, decimalPlaces = null) {
  const num = typeof value === "number" ? value : parseFloat(value);
  if (Number.isNaN(num)) {
    return 0;
  }
  const d =
    decimalPlaces !== undefined && decimalPlaces !== null
      ? Math.max(0, Math.min(20, Number(decimalPlaces) || 0))
      : getCalculationDecimalPlaces();
  if (d === 0) {
    return Math.round(num + Number.EPSILON);
  }
  const f = 10 ** d;
  return Math.round((num + Number.EPSILON) * f) / f;
}

